import { fetchUtils } from 'react-admin';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
const stepUrl = `${apiUrl}/QmsWorkflowStep`;
const templateUrl = `${apiUrl}/QmsWorkflowTemplate`;
interface ApprovalStep {
  id?: string;
  stepNo: number;
  Name: string;
  stepType: 'single' | 'parallel' | 'conditional';
  approvers: any[];
  approvalRule: 'all' | 'any' | 'majority';
  timeLimit?: number;
  description: string;
  isOptional: boolean;
  instanceId?: string;
}

interface ApprovalTemplate {
  id?: string;
  Name: string;
  docType: string;
  description: string;
  steps: ApprovalStep[];
  isActive: boolean;
  createdDate: string;
  createdBy: number;
  lastModified: string;
  usageCount: number;
  stepsLoaded?: boolean;
}

function getHeaders() {
  const headers = new Headers({ Accept: 'application/json' });
  const authToken = localStorage.getItem('session') || localStorage.getItem('token');
  if (authToken) headers.set('Authorization', `Bearer ${authToken}`);
  return headers;
}

function getCurrentUserId(): number | null {
  const raw = localStorage.getItem('userId');
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

function extractApproverIds(approvers: any[]): Array<string | number> {
  if (!Array.isArray(approvers)) return [];
  return approvers
    .map(item => {
      if (item == null) return undefined;
      if (typeof item === 'string' || typeof item === 'number') return item;
      if (typeof item === 'object' && 'id' in item) return (item as any).id;
      return undefined;
    })
    .filter((id): id is string | number => id !== undefined && id !== null);
}

/**
 * Get all workflow templates
 */
export async function fetchAllTemplates(options: { includeSteps?: boolean } = {}): Promise<ApprovalTemplate[]> {
  const { includeSteps = true } = options;
  try {
    const response = await fetchUtils.fetchJson(
      `${templateUrl}`,
      { headers: getHeaders() }
    );
    
    const templates = response.json.data || response.json || [];

    if (!includeSteps) {
      return templates.map((template: any) => ({
        ...template,
        Name: template.Name || template.name,
        docType: template.docType || template.DocumentType || '',
        description: template.description || '',
        steps: [],
        stepsLoaded: false
      }));
    }
    
    // Check if templates already include steps data
    // If yes, use it directly; if no, fetch steps separately
    const result = await Promise.all(
      templates.map(async (template: any) => {
        try {
          // Check if template already has steps data
          const existingSteps = template.steps as any[];
          if (Array.isArray(existingSteps) && existingSteps.length > 0) {
            // Steps already included in template, use directly
            return {
              ...template,
              Name: template.Name || template.name,
              description: template.description || '',
              steps: existingSteps.map((step: any, idx: number) => {
                const rawApprovers = step.approvers ?? step.approverIds;
                return {
                  ...step,
                  id: step.id || `s${idx}`,
                  Name: step.Name || step.name || '',
                  stepNo: step.stepNo || idx + 1,
                  approvers: typeof rawApprovers === 'string'
                    ? JSON.parse(rawApprovers)
                    : (Array.isArray(rawApprovers) ? rawApprovers : [])
                };
              }),
              stepsLoaded: true
            };
          }
          
          // If no steps in template, fetch from separate API
          const stepsResponse = await fetchUtils.fetchJson(
            `${stepUrl}/byTemplate/${template.id}`,
            { headers: getHeaders() }
          );
          const steps = stepsResponse.json.data || stepsResponse.json || [];
          
          return {
            ...template,
            Name: template.Name || template.name,
            description: template.description || '',
            steps: steps.map((step: any, idx: number) => {
              const rawApprovers = step.approvers ?? step.approverIds;
              return {
                ...step,
                id: step.id || `s${idx}`,
                Name: step.Name || step.name || '',
                stepNo: step.stepNo || idx + 1,
                approvers: typeof rawApprovers === 'string'
                  ? JSON.parse(rawApprovers)
                  : (Array.isArray(rawApprovers) ? rawApprovers : [])
              };
            }),
            stepsLoaded: true
          };
        } catch (error) {
          console.error(`Failed to fetch steps for template ${template.id}:`, error);
          return { 
            ...template,
            Name: template.Name || template.name,
            docType: template.docType || template.DocumentType || '',
            description: template.description || '',
            steps: [],
            stepsLoaded: false
          };
        }
      })
    );
    
    return result;
  } catch (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }
}

/**
 * Get single template with steps
 */
export async function fetchTemplate(templateId: string): Promise<ApprovalTemplate> {
  try {
    const response = await fetchUtils.fetchJson(
      `${templateUrl}/${templateId}`,
      { headers: getHeaders() }
    );
    
    const template = response.json.data || response.json;
    
    // Check if template already includes steps data
    const existingSteps = template.steps as any[];
    if (Array.isArray(existingSteps) && existingSteps.length > 0) {
      // Steps already included, use directly
      return {
        ...template,
        Name: template.Name || template.name,
        docType: template.docType || template.DocumentType || '',
        description: template.description || '',
        steps: existingSteps.map((step: any, idx: number) => {
          const rawApprovers = step.approvers ?? step.approverIds;
          return {
            ...step,
            id: step.id || `s${idx}`,
            Name: step.Name || step.name || '',
            stepNo: step.stepNo || idx + 1,
            approvers: typeof rawApprovers === 'string'
              ? JSON.parse(rawApprovers)
              : (Array.isArray(rawApprovers) ? rawApprovers : [])
          };
        }),
        stepsLoaded: true
      };
    }
    
    // If no steps in template, fetch from separate API
    const stepsResponse = await fetchUtils.fetchJson(
      `${stepUrl}/byTemplate/${templateId}`,
      { headers: getHeaders() }
    );
    
    const steps = stepsResponse.json.data || stepsResponse.json || [];
    
    return {
      ...template,
      Name: template.Name || template.name,
      docType: template.docType || template.DocumentType || '',
      description: template.description || '',
      steps: steps.map((step: any, idx: number) => {
        const rawApprovers = step.approvers ?? step.approverIds;
        return {
          ...step,
          id: step.id || `s${idx}`,
          Name: step.Name || step.name || '',
          stepNo: step.stepNo || idx + 1,
          approvers: typeof rawApprovers === 'string'
            ? JSON.parse(rawApprovers)
            : (Array.isArray(rawApprovers) ? rawApprovers : [])
        };
      }),
      stepsLoaded: true
    };
  } catch (error) {
    console.error('Error fetching template:', error);
    throw error;
  }
}

/**
 * Create new template with steps
 */
export async function createTemplate(template: ApprovalTemplate): Promise<ApprovalTemplate> {
  try {
    // Create template
    const createdByValue = Number.isFinite(Number(template.createdBy))
      ? Number(template.createdBy)
      : (getCurrentUserId() ?? 0);

    const templateData = {
      name: template.Name,
      docType: template.docType,
      description: template.description,
      isActive: template.isActive,
      createdDate: template.createdDate,
      createdBy: createdByValue,
      lastModified: template.lastModified,
      usageCount: template.usageCount
    };

    const templateResponse = await fetchUtils.fetchJson(
      `${templateUrl}`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(templateData)
      }
    );

    const createdTemplate = templateResponse.json.data || templateResponse.json;
    const templateId = createdTemplate.id;

    // Create steps
    if (template.steps && template.steps.length > 0) {
      await Promise.all(
        template.steps.map(step =>
          fetchUtils.fetchJson(
            `${stepUrl}`,
            {
              method: 'POST',
              headers: getHeaders(),
              body: JSON.stringify({
                  TemplateId: templateId,
                  stepNo: step.stepNo,
                  Name: step.Name,
                  stepType: step.stepType,
                  approverIds: extractApproverIds(step.approvers || []),
                  approvalRule: step.approvalRule,
                  timeLimit: step.timeLimit || 0,
                  description: step.description,
                  isOptional: step.isOptional
                  // instanceId: step.instanceId || ''
                }
              )
            }
          )
        )
      );
    }

    // Return the newly created template with steps
    return {
      ...createdTemplate,
      Name: createdTemplate.Name || createdTemplate.name,
      docType: createdTemplate.docType || createdTemplate.DocumentType || '',
      description: createdTemplate.description || '',
      steps: template.steps || [],
      isActive: template.isActive,
      createdDate: template.createdDate,
      createdBy: createdByValue,
      lastModified: template.lastModified,
      usageCount: template.usageCount
    };
  } catch (error) {
    console.error('Error creating template:', error);
    throw error;
  }
}

/**
 * Update template with steps
 */
export async function updateTemplate(template: ApprovalTemplate): Promise<ApprovalTemplate> {
  try {
    // Update template
    const createdByValue = Number.isFinite(Number(template.createdBy))
      ? Number(template.createdBy)
      : (getCurrentUserId() ?? 0);

    const templateData = {
      name: template.Name,
      docType: template.docType,
      description: template.description,
      isActive: template.isActive,
      createdDate: template.createdDate,
      createdBy: createdByValue,
      lastModified: new Date().toISOString().split('T')[0],
      usageCount: template.usageCount
    };

    await fetchUtils.fetchJson(
      `${templateUrl}/${template.id}`,
      {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(templateData)
      }
    );

    // Get existing steps from database
    const stepsResponse = await fetchUtils.fetchJson(
      `${stepUrl}/byTemplate/${template.id}`,
      { headers: getHeaders() }
    );
    
    const existingSteps = stepsResponse.json.data || stepsResponse.json || [];
    const existingStepIds = new Set(existingSteps.map((s: ApprovalStep) => s.id));

    // Process steps: distinguish between new steps (with temp id like 's1') and existing steps (with numeric id)
    const stepsToCreate = template.steps?.filter(s => s.id && typeof s.id === 'string' && s.id.startsWith('s')) || [];
    const stepsToUpdate = template.steps?.filter(s => s.id && !String(s.id).startsWith('s')) || [];
    const newStepIds = new Set(stepsToUpdate.map(s => s.id));

    // Delete steps that were removed
    const stepsToDelete = existingSteps.filter((s: ApprovalStep) => !newStepIds.has(s.id));
    await Promise.all(
      stepsToDelete.map((step: ApprovalStep) =>
        fetchUtils.fetchJson(
          `${stepUrl}/${step.id}`,
          {
            method: 'DELETE',
            headers: getHeaders()
          }
        )
      )
    );

    // Update existing steps
    await Promise.all(
      stepsToUpdate.map(step =>
        fetchUtils.fetchJson(
          `${stepUrl}/${step.id}`,
          {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({
              stepNo: step.stepNo,
              Name: step.Name,
              stepType: step.stepType,
              approverIds: extractApproverIds(step.approvers || []),
              approvalRule: step.approvalRule,
              timeLimit: step.timeLimit || 0,
              description: step.description,
              isOptional: step.isOptional
            })
          }
        )
      )
    );

    // Create new steps
    if (stepsToCreate.length > 0) {
      await Promise.all(
        stepsToCreate.map(step =>
          fetchUtils.fetchJson(
            `${stepUrl}`,
            {
              method: 'POST',
              headers: getHeaders(),
              body: JSON.stringify({
                TemplateId: template.id,
                stepNo: step.stepNo,
                Name: step.Name,
                stepType: step.stepType,
                approverIds: extractApproverIds(step.approvers || []),
                approvalRule: step.approvalRule,
                timeLimit: step.timeLimit || 0,
                description: step.description,
                isOptional: step.isOptional
              })
            }
          )
        )
      );
    }

    return fetchTemplate(template.id || '');
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
}

/**
 * Delete template and its steps
 */
export async function deleteTemplate(templateId: string): Promise<void> {
  try {
    // Delete all steps
    const stepsResponse = await fetchUtils.fetchJson(
      `${stepUrl}/byTemplate/${templateId}`,
      { headers: getHeaders() }
    );
    
    const steps = stepsResponse.json.data || stepsResponse.json || [];
    
    await Promise.all(
      steps.map((step: ApprovalStep) =>
        fetchUtils.fetchJson(
          `${stepUrl}/${step.id}`,
          {
            method: 'DELETE',
            headers: getHeaders()
          }
        )
      )
    );

    // Delete template
    await fetchUtils.fetchJson(
      `${templateUrl}/${templateId}`,
      {
        method: 'DELETE',
        headers: getHeaders()
      }
    );
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
}
